import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { TierProgressBar } from '@/components/engagement/TierProgressBar';
import { InclusiveBadgeDisplay } from '@/components/engagement/InclusiveBadgeDisplay';
import { QuietModeToggle } from '@/components/engagement/QuietModeToggle';
import { RewardClaimCard } from '@/components/engagement/RewardClaimCard';
import { useEngagement } from '@/hooks/useEngagement';
import { useQuietMode } from '@/hooks/useQuietMode';
import { useLanguage } from '@/i18n/LanguageContext';
import { Sparkles, Trophy } from 'lucide-react';

interface EngagementCardProps {
  className?: string;
}

export function EngagementCard({ className }: EngagementCardProps) {
  const { t } = useLanguage();
  const {
    earnedBadges,
    unearnedBadges,
    currentTier,
    currentTierConfig,
    nextTierConfig,
    totalPoints,
    pointsToNextTier,
    pendingRewards,
    claimReward,
    isLoading,
  } = useEngagement();

  const { quietMode, toggleQuietMode } = useQuietMode();

  const getTierName = (tierName: string) => {
    const tierKey = tierName.toLowerCase() as 'awareness' | 'presence' | 'continuity' | 'beyond';
    return t.howToUse.gamification.tiers[tierKey] || tierName;
  };

  const activeReward = pendingRewards[0];

  if (isLoading) {
    return (
      <Card className={`${className} glass border-0 rounded-2xl`}>
        <CardContent className="py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-3 bg-muted rounded" />
            <div className="h-8 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quietMode) {
    return (
      <Card className={`${className} glass border-0 rounded-2xl`}>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground">{t.howToUse.gamification.quietModeOn}</p>
            <QuietModeToggle
              enabled={quietMode}
              onToggle={toggleQuietMode}
              variant="compact"
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {activeReward && (
          <RewardClaimCard
            tierCompleted={activeReward.tier_completed || 'awareness'}
            credits={activeReward.credits_amount}
            discount={activeReward.discount_percent}
            legacyBuilder={activeReward.reward_type === 'legacy_status'}
            onClaim={() => claimReward(activeReward.id)}
          />
        )}
      </AnimatePresence>

      <Card className={`${className} overflow-hidden border-0 shadow-soft rounded-2xl`}>
        {/* Gradient Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-accent via-accent/90 to-accent/70 p-5 text-accent-foreground">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.1),transparent)] pointer-events-none" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                className="p-2.5 bg-white/15 rounded-xl backdrop-blur-sm"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.1 }}
              >
                <Sparkles className="h-6 w-6" />
              </motion.div>
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  {getTierName(currentTierConfig.displayName)}
                  <span className="text-sm font-normal opacity-80">
                    ✨ {totalPoints} pts
                  </span>
                </h3>
                <p className="text-white/75 text-sm">
                  {nextTierConfig 
                    ? t.howToUse.gamification.ptsTo
                        .replace('{points}', String(pointsToNextTier))
                        .replace('{tier}', getTierName(nextTierConfig.displayName))
                    : t.howToUse.gamification.highestTier
                  }
                </p>
              </div>
            </div>
            <QuietModeToggle
              enabled={quietMode}
              onToggle={toggleQuietMode}
              variant="compact"
            />
          </div>
        </div>

        <CardContent className="p-5 space-y-5">
          <TierProgressBar
            currentTier={currentTier}
            totalPoints={totalPoints}
          />

          <InclusiveBadgeDisplay
            earnedBadges={earnedBadges}
            unearnedBadges={unearnedBadges}
          />

          <motion.div 
            className="rounded-xl bg-gradient-to-r from-primary/8 to-accent/8 border border-primary/15 p-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3">
              <Trophy className="h-5 w-5 text-primary shrink-0" />
              <p className="text-sm text-foreground">
                {earnedBadges.length === 0 ? '💚 ' : '💪 '}
                {t.howToUse.gamification.progressEncouragement}
              </p>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </div>
  );
}
